import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ChevronRight, Edit, History, PanelRight, PanelRightClose, Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { TooltipProvider } from "../../components/ui/tooltip";
import { useAnnotationVersion } from "../../hooks/useAnnotationVersion";
import { usePresence } from "../../hooks/usePresence";
import { type Annotation, api } from "../../lib/api";
import { formatRelative } from "../../lib/utils";
import { useReading } from "../../stores/reading";
import { useUI } from "../../stores/ui";
import type { AnnotationRange, HighlightColor } from "../../types/api";
import { AddToNotebookPopover } from "../notebooks/AddToNotebookPopover";
import { type AddToNotebookInput, useAddToNotebook } from "../notebooks/useAddToNotebook";
import { AnnotationsLayer } from "./AnnotationsLayer";
import { CommentForm } from "./CommentForm";
import { DocAnnotationsDrawer } from "./DocAnnotationsDrawer";
import { type HeadingActionKind, HeadingAffordances } from "./HeadingAffordances";
import { InlineOverlay, type Overlay } from "./InlineOverlay";
import { PresenceIndicator } from "./PresenceIndicator";
import { ProseBody } from "./ProseBody";
import { ReadingProgress } from "./ReadingProgress";
import { SelectionMenu, type SelectionState } from "./SelectionMenu";
import { TagDrawer } from "./TagDrawer";
import { TocPanel } from "./TocPanel";
import { VersionDrawer } from "./VersionDrawer";
import { ViewerAiPanel } from "./ViewerAiPanel";
import { mdToHtml } from "./markdown/mdToHtml";

type AnnotatableSelection = SelectionState & { annotationRange: AnnotationRange };

export function DocViewer() {
  const { docId } = useParams({ strict: false }) as { docId: string };
  // B1.4 — keep doc/markdown/sections stable across focus changes so that
  // dangerouslySetInnerHTML doesn't replace the prose-root (which would wipe
  // the marks injected by AnnotationsLayer).
  const docQuery = useQuery({
    queryKey: ["doc", docId],
    queryFn: () => api.getDoc(docId),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const sectionsQuery = useQuery({
    queryKey: ["doc-sections", docId],
    queryFn: () => api.getDocSections(docId),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const markdownQuery = useQuery({
    queryKey: ["doc-markdown", docId],
    queryFn: () => api.getDocMarkdown(docId),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const [activeSecId, setActiveSecId] = useState<string | undefined>(undefined);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [overlays, setOverlays] = useState<Record<string, Overlay[]>>({});
  const [selection, setSelection] = useState<SelectionState | null>(null);
  const [commentTarget, setCommentTarget] = useState<AnnotatableSelection | null>(null);
  const [tagTarget, setTagTarget] = useState<AnnotatableSelection | null>(null);
  const [pendingAnnotations, setPendingAnnotations] = useState<Annotation[]>([]);
  const [notebookPicker, setNotebookPicker] = useState<{
    item: AddToNotebookInput;
    anchor: { top: number; left: number };
  } | null>(null);

  const qc = useQueryClient();
  const setReadingPos = useReading((s) => s.setPosition);
  const readingMode = useUI((s) => s.readingMode);
  const marginChips = useUI((s) => s.marginChips);
  const toggleMarginChips = useUI((s) => s.toggleMarginChips);
  const [versionOpen, setVersionOpen] = useState(false);
  const [annotationsOpen, setAnnotationsOpen] = useState(false);
  // C1.4 — TOC ancho persistido en localStorage
  const [tocWidth, setTocWidth] = useState<number>(() => {
    if (typeof window === "undefined") return 224;
    const raw = window.localStorage.getItem("docustudio-toc-width");
    const n = raw ? Number.parseInt(raw, 10) : Number.NaN;
    return Number.isFinite(n) && n >= 160 && n <= 480 ? n : 224;
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("docustudio-toc-width", String(tocWidth));
    } catch {
      // ignore
    }
  }, [tocWidth]);
  const { addToLast } = useAddToNotebook();

  // S4 — presence & collaborative annotations polling.
  // Polling only kicks in when another reader is in the doc (presence > 0
  // besides the current user) to keep idle-doc cost flat.
  const presenceQuery = usePresence(docId, activeSecId);
  const otherReaders = presenceQuery.data?.items ?? [];
  useAnnotationVersion(docId, otherReaders.length > 0);

  const htmlData = useQuery({
    queryKey: ["doc-html", docId, markdownQuery.data],
    enabled: !!markdownQuery.data,
    queryFn: async () => mdToHtml(markdownQuery.data ?? ""),
  });

  const createAnnotation = useMutation({
    mutationFn: (body: {
      kind: "highlight";
      color: HighlightColor;
      range: AnnotationRange;
      sectionId?: string;
    }) => {
      const versionId = docQuery.data?.current_version_id;
      if (!versionId) throw new Error("document version missing");
      return api.createAnnotation(docId, {
        kind: body.kind,
        color: body.color,
        range: body.range,
        section_id: body.sectionId,
        version_id: versionId,
      });
    },
  });

  useEffect(() => {
    if (activeSecId) setReadingPos(docId, activeSecId);
  }, [activeSecId, docId, setReadingPos]);

  useEffect(() => {
    if (!htmlData.data || !sectionsQuery.data) return;
    const hash = decodeURIComponent(window.location.hash.replace(/^#/, ""));
    if (!hash) return;
    const timeout = window.setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ block: "start" });
        setActiveSecId(hash);
      }
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [htmlData.data, sectionsQuery.data]);

  useEffect(() => {
    function onMouseUp(e: MouseEvent) {
      if (
        e.target instanceof Element &&
        e.target.closest("[data-selection-menu], [data-ann-chip]")
      ) {
        return;
      }
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) {
        setSelection(null);
        return;
      }
      const text = sel.toString().trim();
      if (text.length < 3) {
        setSelection(null);
        return;
      }
      const anchorNode = sel.anchorNode;
      if (!anchorNode) return;
      const proseEl = (
        anchorNode.nodeType === Node.ELEMENT_NODE
          ? (anchorNode as Element)
          : anchorNode.parentElement
      )?.closest("[data-prose]");
      if (!proseEl) {
        setSelection(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      const annotation = getAnnotationRangeFromSelection(range);
      const startBlock = getSelectionBlock(range.startContainer);
      setSelection({
        rect: { top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width },
        text,
        annotationRange: annotation?.range,
        sectionId:
          annotation?.sectionId ?? (startBlock ? getSelectionSectionId(startBlock) : undefined),
      });
    }
    document.addEventListener("mouseup", onMouseUp);
    return () => document.removeEventListener("mouseup", onMouseUp);
  }, []);

  useEffect(() => {
    function onCopy(e: ClipboardEvent) {
      if (!selection) return;
      if (isEditableTarget(e.target)) return;
      if (!e.clipboardData) return;
      e.clipboardData.setData("text/plain", selection.text);
      e.preventDefault();
      toast.success("Copiado");
    }
    document.addEventListener("copy", onCopy);
    return () => document.removeEventListener("copy", onCopy);
  }, [selection]);

  // Keyboard shortcuts for "add to notebook"
  // ⌘N (Ctrl+N on linux/win) → add to last notebook directly (no popover)
  // ⌘⇧N → open picker popover
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      const cmd = e.metaKey || e.ctrlKey;
      if (!cmd || e.key.toLowerCase() !== "n") return;
      if (!selection || !selection.text) return;
      const item: AddToNotebookInput = {
        item_type: "selection",
        source_doc_id: docId,
        source_section_id: selection.sectionId,
        source_version_id: doc?.current_version_id ?? undefined,
        selected_text: selection.text,
        title: truncate(selection.text, 80),
      };
      e.preventDefault();
      if (e.shiftKey) {
        setNotebookPicker({
          item,
          anchor: { top: selection.rect.bottom, left: selection.rect.left },
        });
        setSelection(null);
        return;
      }
      addToLast.mutate(item, {
        onSuccess: (result) =>
          toast.success("Añadido a apuntes", { description: result.notebookTitle }),
        onError: (err) => toast.error(`No se pudo añadir: ${err}`),
      });
      setSelection(null);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selection, docId, addToLast]);

  // B5.3 — viewer hotkeys (cmd + H/,/L/E/R/T) acting on the current selection
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      const cmd = e.metaKey || e.ctrlKey;
      if (!cmd) return;
      if (!selection) return;
      const k = e.key.toLowerCase();
      let action: string | null = null;
      if (k === "h") action = "highlight";
      else if (k === ",") action = "comment";
      else if (k === "l") action = "tag";
      else if (k === "e") action = "explain";
      else if (k === "r") action = "summarize";
      else if (k === "t") action = "translate";
      if (!action) return;
      e.preventDefault();
      handleSelectionAction(action);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  function toggleSection(secId: string) {
    if (secId === "all-h2") {
      const next: Record<string, boolean> = {};
      for (const s of sectionsQuery.data?.items ?? []) {
        if (s.heading_level === 2) next[s.id] = true;
      }
      setCollapsed(next);
      return;
    }
    if (secId === "expand-all") {
      setCollapsed({});
      return;
    }
    setCollapsed((p) => ({ ...p, [secId]: !p[secId] }));
  }

  function addOverlay(secId: string, ov: Overlay) {
    setOverlays((p) => ({ ...p, [secId]: [...(p[secId] ?? []), ov] }));
  }

  function closeOverlay(secId: string, idx: number) {
    setOverlays((p) => {
      const list = (p[secId] ?? []).slice();
      list.splice(idx, 1);
      return { ...p, [secId]: list };
    });
  }

  function jumpToSection(id: string): void {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      history.replaceState(null, "", `#${encodeURIComponent(id)}`);
    }
    setActiveSecId(id);
  }

  function handleSelectionAction(kind: string, extra?: string): void {
    if (!selection) return;
    const text = selection.text;
    const secId = selection.sectionId ?? activeSecId ?? "default";
    if (kind === "highlight") {
      if (!selection.annotationRange) {
        toast.error("Para subrayar, selecciona texto dentro de un mismo bloque.");
        setSelection(null);
        return;
      }
      const color = (extra ?? "yellow") as HighlightColor;
      const pending = makePendingHighlight({
        docId,
        versionId: doc?.current_version_id ?? "",
        color,
        range: selection.annotationRange,
        sectionId: selection.sectionId,
      });
      setPendingAnnotations((current) => [...current, pending]);
      createAnnotation.mutate(
        {
          kind: "highlight",
          color,
          range: selection.annotationRange,
          sectionId: selection.sectionId,
        },
        {
          onSuccess: (result) => {
            setPendingAnnotations((current) => current.filter((item) => item.id !== pending.id));
            qc.setQueryData<{ items: Annotation[] }>(["doc-annotations", docId], (current) => ({
              items: [
                ...(current?.items ?? []).filter((item) => item.id !== result.id),
                { ...pending, id: result.id, updated_at: new Date().toISOString() },
              ],
            }));
            toast.success(`Subrayado en ${spanishColor(color)}`);
            // B1.4 — no `invalidateQueries` here; the optimistic setQueryData
            // already has the canonical row. Re-fetching at +600ms used to
            // race and clear marks on slow networks.
          },
          onError: (err) => {
            setPendingAnnotations((current) => current.filter((item) => item.id !== pending.id));
            toast.error(`No se pudo guardar el subrayado: ${err}`);
          },
        },
      );
      setSelection(null);
    } else if (kind === "comment") {
      if (!selection.annotationRange) {
        toast.error("Para comentar, selecciona texto dentro de un mismo bloque.");
        setSelection(null);
        return;
      }
      setCommentTarget(selection as AnnotatableSelection);
      setSelection(null);
    } else if (kind === "tag") {
      if (!selection.annotationRange) {
        toast.error("Para etiquetar, selecciona texto dentro de un mismo bloque.");
        setSelection(null);
        return;
      }
      setTagTarget(selection as AnnotatableSelection);
      setSelection(null);
    } else if (kind === "notebook") {
      const item: AddToNotebookInput = {
        item_type: "selection",
        source_doc_id: docId,
        source_section_id: selection.sectionId,
        source_version_id: doc?.current_version_id ?? undefined,
        selected_text: text,
        title: truncate(text, 80),
      };
      // Open the picker anchored where the selection menu was (Readwise pattern).
      setNotebookPicker({
        item,
        anchor: { top: selection.rect.bottom, left: selection.rect.left },
      });
      setSelection(null);
    } else if (kind === "translate") {
      addOverlay(secId, {
        kind: "translation",
        title: "Traducción de selección",
        lang: `${extra ?? "EN"} <- auto`,
        streaming: true,
        content: `Traduciendo "${truncate(text, 100)}"...`,
      });
      setSelection(null);
      window.setTimeout(() => {
        setOverlays((p) => {
          const list = (p[secId] ?? []).slice();
          const i = list.findIndex((o) => o.streaming);
          if (i >= 0) {
            const item = list[i];
            if (item) {
              list[i] = {
                ...item,
                streaming: false,
                content: `(${extra ?? "EN"}) Translation of: "${truncate(text, 240)}"`,
              };
            }
          }
          return { ...p, [secId]: list };
        });
      }, 900);
    } else if (kind === "summarize") {
      addOverlay(secId, {
        kind: "summary",
        title: "Resumen",
        streaming: true,
        content: "Generando...",
      });
      setSelection(null);
      window.setTimeout(() => {
        setOverlays((p) => {
          const list = (p[secId] ?? []).slice();
          const i = list.findIndex((o) => o.streaming);
          if (i >= 0) {
            const item = list[i];
            if (item) list[i] = { ...item, streaming: false, content: makeFakeSummary(text) };
          }
          return { ...p, [secId]: list };
        });
      }, 900);
    } else if (kind === "explain") {
      addOverlay(secId, {
        kind: "explain",
        title: "Explicación",
        streaming: true,
        content: "Pensando...",
      });
      setSelection(null);
      window.setTimeout(() => {
        setOverlays((p) => {
          const list = (p[secId] ?? []).slice();
          const i = list.findIndex((o) => o.streaming);
          if (i >= 0) {
            const item = list[i];
            if (item) list[i] = { ...item, streaming: false, content: makeFakeExplanation(text) };
          }
          return { ...p, [secId]: list };
        });
      }, 900);
    } else if (kind === "copy") {
      void copySelectedText(text);
      setSelection(null);
    } else {
      toast.message(`Acción: ${kind}`);
      setSelection(null);
    }
  }

  if (docQuery.isLoading || markdownQuery.isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-8">
        <Skeleton className="mb-4 h-10 w-1/2" />
        <Skeleton className="mb-2 h-4 w-full" />
        <Skeleton className="mb-2 h-4 w-full" />
        <Skeleton className="mb-2 h-4 w-3/4" />
      </div>
    );
  }

  const doc = docQuery.data;
  const sections = sectionsQuery.data?.items ?? [];
  const html = htmlData.data?.html ?? "";

  return (
    <TooltipProvider>
      <div className="flex h-full overflow-hidden">
        {!readingMode && (
          <>
            <aside
              data-testid="doc-toc-aside"
              className="shrink-0 border-r border-(--border-subtle) bg-(--bg-elevated) overflow-y-auto p-3"
              style={{ width: `${tocWidth}px` }}
            >
              <TocPanel
                sections={sections}
                activeId={activeSecId}
                collapsed={collapsed}
                onJump={jumpToSection}
                onToggleCollapse={toggleSection}
              />
            </aside>
            <TocResizer initialWidth={tocWidth} onResize={setTocWidth} />
          </>
        )}

        <main className="flex-1 overflow-y-auto px-6 py-6" id="doc-main-content">
          <ReadingProgress targetId="doc-main-content" />
          {doc && (
            <header className="mx-auto max-w-3xl mb-6" data-testid="doc-header">
              <nav
                className="flex items-center gap-1 text-xs text-(--text-tertiary)"
                data-testid="doc-breadcrumb"
                aria-label="Ruta"
              >
                <Link to="/" className="hover:text-(--text-primary)">
                  Inicio
                </Link>
                <ChevronRight size={11} />
                <Link to="/docs" className="hover:text-(--text-primary)">
                  {doc.project}
                </Link>
                {doc.kind && (
                  <>
                    <ChevronRight size={11} />
                    <span>{doc.kind}</span>
                  </>
                )}
                <ChevronRight size={11} />
                <span className="truncate text-(--text-secondary)">{doc.path}</span>
              </nav>
              <h1 className="mt-2 text-3xl font-bold leading-tight" data-testid="doc-title">
                {doc.title}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="doc-tags">
                {doc.tags.map((t) => (
                  <Badge key={t} variant="default" className="text-[10px]">
                    #{t}
                  </Badge>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-(--text-tertiary)">
                {doc.owner_subject && <span data-testid="doc-author">{doc.owner_subject}</span>}
                <span>· {doc.lang}</span>
                <span>· {doc.status}</span>
                {doc.criticality && (
                  <span data-testid="doc-criticality">· criticidad {doc.criticality}</span>
                )}
                <span>· actualizado {formatRelative(doc.updated_at)}</span>
                <span className="ml-auto">
                  <PresenceIndicator docId={docId} selfSubject={null} />
                </span>
              </div>
              <div className="mt-4 flex gap-2" data-testid="doc-header-actions">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setVersionOpen(true)}
                  data-testid="doc-action-history"
                >
                  <History size={13} /> Historial
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setAnnotationsOpen(true)}
                  data-testid="doc-action-annotations"
                >
                  <Edit size={13} /> Anotaciones
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => useUI.getState().setReadingMode(!readingMode)}
                >
                  {readingMode ? "Salir lectura" : "Lectura limpia"}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => toggleMarginChips()}
                  data-testid="margin-chips-toggle-header"
                  data-state={marginChips ? "on" : "off"}
                  aria-pressed={marginChips}
                  title={
                    marginChips
                      ? "Ocultar chips de anotaciones del margen"
                      : "Mostrar chips de anotaciones del margen"
                  }
                >
                  {marginChips ? <PanelRightClose size={13} /> : <PanelRight size={13} />}
                  Margen {marginChips ? "on" : "off"}
                </Button>
                <Button variant="secondary" size="sm">
                  <Share2 size={13} /> Compartir
                </Button>
                <Button variant="primary" size="sm" className="ml-auto" asChild>
                  <Link to="/docs/$docId/edit" params={{ docId }}>
                    <Edit size={13} /> Editar
                  </Link>
                </Button>
              </div>
            </header>
          )}

          <div className="mx-auto max-w-3xl">
            <ProseBody
              html={html}
              sections={sections}
              collapsed={collapsed}
              onSetActive={setActiveSecId}
              onToggleCollapse={toggleSection}
              overlays={overlays}
              onOverlayClose={closeOverlay}
            />
            <AnnotationsLayer
              docId={docId}
              versionId={doc?.current_version_id ?? ""}
              pendingAnnotations={pendingAnnotations}
            />
            <HeadingAffordances
              sections={sections}
              collapsed={collapsed}
              onToggleCollapse={toggleSection}
              onAction={(kind: HeadingActionKind, sectionId: string) => {
                if (kind === "copy-link") {
                  const link = `${window.location.origin}${window.location.pathname}#${encodeURIComponent(sectionId)}`;
                  void navigator.clipboard.writeText(link);
                  toast.success("Enlace copiado");
                } else if (kind === "summarize-sec") {
                  addOverlay(sectionId, {
                    kind: "summary",
                    title: "Resumen",
                    streaming: true,
                    content: "Generando…",
                  });
                  window.setTimeout(() => {
                    setOverlays((p) => {
                      const list = (p[sectionId] ?? []).slice();
                      const i = list.findIndex((o) => o.streaming);
                      const item = list[i];
                      if (item) {
                        list[i] = {
                          ...item,
                          streaming: false,
                          content: `Resumen de la sección "${sectionId}" — fase 1.`,
                        };
                      }
                      return { ...p, [sectionId]: list };
                    });
                  }, 800);
                } else if (kind === "translate-sec") {
                  addOverlay(sectionId, {
                    kind: "translation",
                    title: "Traducción",
                    lang: "EN ← auto",
                    streaming: true,
                    content: "Translating…",
                  });
                  window.setTimeout(() => {
                    setOverlays((p) => {
                      const list = (p[sectionId] ?? []).slice();
                      const i = list.findIndex((o) => o.streaming);
                      const item = list[i];
                      if (item) {
                        list[i] = {
                          ...item,
                          streaming: false,
                          content: `(EN) translation of section ${sectionId}`,
                        };
                      }
                      return { ...p, [sectionId]: list };
                    });
                  }, 800);
                } else if (kind === "comments") {
                  toast.message("Comentarios en la sección — abre los chips inline");
                } else if (kind === "tags") {
                  toast.message("Etiquetas de la sección — abre los chips inline");
                } else if (kind === "more") {
                  toast.message("Acciones extra — disponibles en B5");
                }
              }}
            />
            <InlineOverlay /* mount-only, content rendered by ProseBody */ />
          </div>
        </main>

        {!readingMode && (
          <ViewerAiPanel docId={docId} activeSecId={activeSecId} sections={sections} />
        )}

        {selection && (
          <SelectionMenu
            rect={selection.rect}
            onAction={handleSelectionAction}
            onClose={() => setSelection(null)}
          />
        )}

        {commentTarget && (
          <CommentForm
            docId={docId}
            versionId={doc?.current_version_id ?? ""}
            quote={commentTarget.text}
            range={commentTarget.annotationRange}
            sectionId={commentTarget.sectionId}
            onClose={() => setCommentTarget(null)}
          />
        )}

        {tagTarget && (
          <TagDrawer
            docId={docId}
            versionId={doc?.current_version_id ?? ""}
            quote={tagTarget.text}
            range={tagTarget.annotationRange}
            sectionId={tagTarget.sectionId}
            onClose={() => setTagTarget(null)}
          />
        )}

        <VersionDrawer
          open={versionOpen}
          onClose={() => setVersionOpen(false)}
          docTitle={doc?.title}
          docId={docId}
        />

        <DocAnnotationsDrawer
          open={annotationsOpen}
          onClose={() => setAnnotationsOpen(false)}
          docId={docId}
        />

        {notebookPicker && (
          <AddToNotebookPopover
            open
            item={notebookPicker.item}
            onOpenChange={(o) => !o && setNotebookPicker(null)}
            onAdded={(info) =>
              toast.success("Añadido a apuntes", { description: info.notebookTitle })
            }
            trigger={
              <span
                aria-hidden="true"
                style={{
                  position: "fixed",
                  top: notebookPicker.anchor.top,
                  left: notebookPicker.anchor.left,
                  width: 1,
                  height: 1,
                  pointerEvents: "none",
                }}
              />
            }
          />
        )}
      </div>
    </TooltipProvider>
  );
}

function spanishColor(c: string): string {
  return (
    { yellow: "amarillo", green: "verde", pink: "rosa", blue: "azul", purple: "púrpura" }[c] ??
    "amarillo"
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}...` : s;
}

function makeFakeSummary(text: string): string {
  return `Resumen del fragmento: ${truncate(text.replace(/\s+/g, " "), 240)}`;
}

function makeFakeExplanation(text: string): string {
  return `En términos sencillos: este fragmento describe un paso operativo. Si te lo encuentras con una alerta activa, ejecuta el comando con el contexto guardado para reaccionar rápido. Texto: "${truncate(text, 100)}".`;
}

function makePendingHighlight({
  docId,
  versionId,
  color,
  range,
  sectionId,
}: {
  docId: string;
  versionId: string;
  color: HighlightColor;
  range: AnnotationRange;
  sectionId?: string;
}): Annotation {
  const now = new Date().toISOString();
  return {
    id: `ann_pending_${now.replace(/\D/g, "")}_${Math.random().toString(36).slice(2, 8)}`,
    doc_id: docId,
    section_id: sectionId ?? null,
    version_id: versionId,
    author_subject: "me",
    kind: "highlight",
    color,
    body: null,
    range_json: JSON.stringify(range),
    thread_id: null,
    status: "active",
    created_at: now,
    updated_at: now,
  };
}

function getAnnotationRangeFromSelection(
  range: Range,
): { range: AnnotationRange; sectionId?: string } | null {
  const startBlock = getSelectionBlock(range.startContainer);
  const endBlock = getSelectionBlock(range.endContainer);
  if (!startBlock || !endBlock || startBlock !== endBlock) return null;

  const beforeStart = document.createRange();
  beforeStart.selectNodeContents(startBlock);
  beforeStart.setEnd(range.startContainer, range.startOffset);

  const beforeEnd = document.createRange();
  beforeEnd.selectNodeContents(startBlock);
  beforeEnd.setEnd(range.endContainer, range.endOffset);

  const start = beforeStart.toString().length;
  const end = beforeEnd.toString().length;
  const blockId = startBlock.dataset.blockId;
  if (!blockId) return null;

  return {
    range: {
      blockId,
      blockType: startBlock.tagName.toLowerCase(),
      charStartInBlock: Math.min(start, end),
      charEndInBlock: Math.max(start, end),
      quote: range.toString().trim(),
    },
    sectionId: getSelectionSectionId(startBlock),
  };
}

function getSelectionBlock(node: Node): HTMLElement | null {
  const el = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  return (
    el?.closest<HTMLElement>(
      "li[data-block-id], p[data-block-id], h1[data-block-id], h2[data-block-id], h3[data-block-id], h4[data-block-id], h5[data-block-id], h6[data-block-id], pre[data-block-id], blockquote[data-block-id], table[data-block-id]",
    ) ??
    el?.closest<HTMLElement>("[data-block-id]") ??
    null
  );
}

function getSelectionSectionId(block: HTMLElement): string | undefined {
  return (
    block.dataset.sectionId ?? block.closest<HTMLElement>("[data-section-id]")?.dataset.sectionId
  );
}

async function copySelectedText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copiado");
  } catch {
    toast.error("No se pudo copiar");
  }
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(target.closest("input, textarea, [contenteditable='true']"));
}

/**
 * C1.4 — Drag-to-resize handle entre el TOC y la columna prose.
 */
function TocResizer({
  initialWidth,
  onResize,
}: {
  initialWidth: number;
  onResize: (next: number) => void;
}) {
  const draggingRef = useState({ current: false })[0];
  function onPointerDown(e: React.PointerEvent) {
    e.preventDefault();
    draggingRef.current = true;
    const startX = e.clientX;
    const startWidth = initialWidth;
    function move(ev: PointerEvent) {
      if (!draggingRef.current) return;
      const delta = ev.clientX - startX;
      const next = Math.min(480, Math.max(160, startWidth + delta));
      onResize(next);
    }
    function up() {
      draggingRef.current = false;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    }
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }
  return (
    <div
      data-testid="doc-toc-resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label="Redimensionar índice"
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") onResize(Math.max(160, initialWidth - 16));
        else if (e.key === "ArrowRight") onResize(Math.min(480, initialWidth + 16));
      }}
      className="w-1 cursor-col-resize bg-transparent hover:bg-(--accent-300) active:bg-(--accent-500) focus:bg-(--accent-500) outline-none transition-colors"
      style={{ flex: "0 0 4px" }}
    />
  );
}
